/*
 * Add color theme colors to the window object
 * so this can be used by the charts and vector maps
 */

const theme = {
  "primary": "#3B7DDD",
  "secondary": "#6c757d",
  "success": "#1cbb8c",
  "info": "#17a2b8",
  "warning": "#fcb92c",
  "danger": "#dc3545",
  "white": "#fff",
  "gray-100": "#f8f9fa",
  "gray-200": "#e9ecef",
  "gray-300": "#dee2e6",
  "gray-400": "#ced4da",
  "gray-500": "#adb5bd",
  "gray-600": "#6c757d",
  "gray-700": "#495057",
  "gray-800": "#343a40",
  "gray-900": "#212529",
  "black": "#000"
};

// Add theme to the window object
window.theme = theme;

const THEME_STORAGE_KEY = "app-theme";
const LEGACY_THEME_STORAGE_KEY = "theme";
const THEMES = ["light", "dark"];

const canUseLocalStorage = () => {
  try {
    const testKey = "__pesaview_theme_test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

const hasLocalStorage = canUseLocalStorage();

const isTheme = value => THEMES.includes(value);

const getStoredTheme = () => {
  if (!hasLocalStorage) {
    return null;
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  const legacyTheme = window.localStorage.getItem(LEGACY_THEME_STORAGE_KEY);

  if (isTheme(storedTheme)) {
    return storedTheme;
  }

  if (isTheme(legacyTheme)) {
    window.localStorage.setItem(THEME_STORAGE_KEY, legacyTheme);
    return legacyTheme;
  }

  return null;
};

const getSystemTheme = () => {
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
};

const getPreferredTheme = () => getStoredTheme() || getSystemTheme();

const persistTheme = selectedTheme => {
  if (!hasLocalStorage) {
    return;
  }

  window.localStorage.setItem(THEME_STORAGE_KEY, selectedTheme);
  window.localStorage.setItem(LEGACY_THEME_STORAGE_KEY, selectedTheme);
};

const updateThemeToggle = selectedTheme => {
  const isDark = selectedTheme === "dark";
  const nextTheme = isDark ? "light" : "dark";
  const currentThemeLabel = selectedTheme.charAt(0).toUpperCase() + selectedTheme.slice(1);

  document.querySelectorAll("[data-theme-toggle]").forEach(toggle => {
    toggle.setAttribute("aria-pressed", String(isDark));
    toggle.setAttribute("aria-label", `Switch to ${nextTheme} mode`);
    toggle.setAttribute("title", `Switch to ${nextTheme} mode`);
    toggle.classList.toggle("is-dark", isDark);

    const label = toggle.querySelector(".theme-toggle-label");

    if (label) {
      label.textContent = currentThemeLabel;
    }
  });
};

const applyTheme = selectedTheme => {
  const resolvedTheme = isTheme(selectedTheme) ? selectedTheme : getSystemTheme();

  document.documentElement.setAttribute("data-theme", resolvedTheme);
  document.documentElement.setAttribute("data-bs-theme", resolvedTheme);
  updateThemeToggle(resolvedTheme);
};

const initThemeToggle = () => {
  applyTheme(getPreferredTheme());

  document.querySelectorAll("[data-theme-toggle]").forEach(toggle => {
    toggle.addEventListener("click", () => {
      const currentTheme = document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
      const nextTheme = currentTheme === "dark" ? "light" : "dark";

      persistTheme(nextTheme);
      applyTheme(nextTheme);
    });
  });

  if (window.matchMedia) {
    const systemThemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = () => {
      if (!getStoredTheme()) {
        applyTheme(getSystemTheme());
      }
    };

    if (systemThemeQuery.addEventListener) {
      systemThemeQuery.addEventListener("change", handleSystemThemeChange);
    } else if (systemThemeQuery.addListener) {
      systemThemeQuery.addListener(handleSystemThemeChange);
    }
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initThemeToggle);
} else {
  initThemeToggle();
}
